import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useGetWaybillShipmentsQuery } from '../../../../api/endpoints/parts';
import { createInitialFilters, convertFiltersToPrimeReactFormat } from '../../../../utils/filterUtils';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { FilterMatchMode } from 'primereact/api';

const useWaybillShipment = () => {
    const { th } = useLanguage();
    const toast = useRef(null);

    const [lazyParams, setLazyParams] = useState({ page: 1, rows: 10 });
    const [apiFilters, setApiFilters] = useState({});
    const [filters, setFilters] = useState({});

    const usageTypeOptions = useMemo(
        () => [
            { label: th('customerOrderType.service'), value: 1 },
            { label: th('customerOrderType.sales'), value: 2 }
        ],
        [th]
    );

    const filterFields = useMemo(
        () => [
            { field: 'dealerName', headerName: th('parts.dealerName'), type: 'text' },
            { field: 'dealerNo', headerName: th('parts.dealerNo'), type: 'text' },
            { field: 'eFaturaNo', headerName: th('parts.eFaturaNo'), type: 'text' },
            { field: 'waybillNo', headerName: th('parts.waybillNo'), type: 'text', options: usageTypeOptions },
            { field: 'orderNo', headerName: th('parts.orderNo'), type: 'text' },
       ],
        [th, usageTypeOptions]
    );

    const filterTypeMap = useMemo(
        () => ({
            dealerName: { type: 'text', matchMode: FilterMatchMode.CONTAINS },
            dealerNo: { type: 'text', matchMode: FilterMatchMode.CONTAINS },
            eFaturaNo: { type: 'text', matchMode: FilterMatchMode.CONTAINS },
            waybillNo: { type: 'text', matchMode: FilterMatchMode.CONTAINS },
            orderNo: { type: 'text', matchMode: FilterMatchMode.CONTAINS }
        }),
        []
    );

    const filterFieldNames = useMemo(() => filterFields.map((f) => f.field), [filterFields]);
    const initialFilters = useMemo(() => createInitialFilters(filterFieldNames, filterTypeMap), [filterFieldNames, filterTypeMap]);

    useEffect(() => setFilters(initialFilters), [initialFilters]);

    const {
        data: waybillShipmentData,
        isLoading
    } = useGetWaybillShipmentsQuery({
        ActiveOnly: 1,
        pageNumber: lazyParams.page,
        pageSize: lazyParams.rows,
        ...apiFilters
    });

  

    const handleApplyFilters = useCallback(
        (updatedFilters) => {
            const converted = convertFiltersToPrimeReactFormat(updatedFilters, filters, filterTypeMap);
            setFilters(converted);

            const newApiFilters = {};
            Object.entries(updatedFilters).forEach(([key, value]) => {
                if (value !== undefined && value !== '' && value !== null) {
                    let processedValue = value;

                    // Handle date fields - use ISO string format like in priceList
                    if ((key === 'startDate' || key === 'endDate') && value instanceof Date) {
                        // Use ISO string format for consistency with priceList filters
                        processedValue = value.toISOString();
                    }
                    if (key === 'activeOnly') {
                        processedValue = value ? 1 : 0;
                    }
                    const pascal = key.charAt(0).toUpperCase() + key.slice(1);
                    newApiFilters[pascal] = processedValue;
                }
            });
            setApiFilters(newApiFilters);
        },
        [filters, filterTypeMap]
    );

    const handleResetFilters = () => {
        setFilters(initialFilters);
        setApiFilters({});
    };

    return {
        toast,
        filterFields,
        customFilterOptions: {
            usageType: usageTypeOptions
        },
        handleSearch: () => {},
        handleApplyFilters,
        handleResetFilters,
        tableProps: {
            data: waybillShipmentData?.items || [],
            totalRecords: waybillShipmentData?.totalCount || 0,
            isLoading,
            onPage: (e) => setLazyParams({ page: e.page + 1, rows: e.rows }),
            lazyParams: { first: (lazyParams.page - 1) * lazyParams.rows, rows: lazyParams.rows },
            resetPagination: false
        }
    };
};

export default useWaybillShipment;
